// Dependencies
import { useEffect, useRef, useState } from "react";
import { useImmer } from "use-immer";
import { z } from "zod";

// Hooks
import useUnsavedChanges from "../useUnsavedChanges";

interface UseFormProps<T> {
	schema: T;
	defaultValues?: any;
	disableWarning?: boolean;
	disableReactWarning?: boolean;
}

interface UseFormError {
	keyPath: (string | number)[];
	message: string;
}

export interface ValidationResult<T> {
	data: T;
	isValid: boolean;
	errors: Record<string, UseFormError>;
}

/**
 * This hook is used to manage the state of a form. It allows you to set the
 * initial state, update the state, and reset the state.
 *
 * The purpose is to provide a simple way to manage the state of a form but
 * it doesn't necessarily need to be used for forms. It's flexible enough to
 * be used for any state management.
 */
const useForm = <T extends z.ZodType>(props: UseFormProps<T>) => {
	const { schema, defaultValues, disableWarning = false, disableReactWarning = false } = props;

	const [stateHasChanged, setStateHasChanged] = useState(false);
	const receivedInitialState = useRef(defaultValues !== undefined);
	const { setIsUnsafe, setIsSafe } = useUnsavedChanges({
		disabled: disableWarning,
		disableReactWarning: disableReactWarning,
	});

	type State = z.infer<T>;

	// hold both states in same immer object
	const [state, updateState] = useImmer<{
		initial: State;
		staging: State;
	}>({
		initial: defaultValues ? defaultValues : ({} as State),
		staging: defaultValues ? defaultValues : ({} as State),
	});

	// recomputes if the state has changed. This is used to determine if the
	// user should be warned about unsaved changes.
	useEffect(() => {
		const changed = JSON.stringify(state.initial) !== JSON.stringify(state.staging);

		if (changed && !stateHasChanged) {
			setIsUnsafe();
			setStateHasChanged(true);
		}
		if (!changed && stateHasChanged) {
			setIsSafe();
			setStateHasChanged(false);
		}
	}, [state.initial, state.staging]);

	/**
	 * Sets the initial state. This happens only once. The purpose is to
	 * replace the useEffect hook with an exhaustive dependency list. For
	 * example:
	 *
	 * useEffect(() => {
	 *     setState({...})
	 * }, [data.name, data.description, date.start, date.end])
	 *
	 * Can now just be replaced with:
	 *
	 * useEffect(() => {
	 *    setInitialState({...})
	 * })
	 */
	const setInitialState = (initialState: State, allowEmpty = false) => {
		if (receivedInitialState.current && !allowEmpty) {
			return;
		}

		updateState((draft) => {
			draft.initial = initialState;
			draft.staging = initialState;
		});

		receivedInitialState.current = true;
	};

	/**
	 * Resets the states (initial and stage) to the new values.
	 */
	const setNewDefaultValues = (newDefaultValues: State) => {
		updateState((draft) => {
			draft.initial = newDefaultValues;
			draft.staging = newDefaultValues;
		});

		receivedInitialState.current = true;
	};

	/**
	 * Sets the staging state equal to the initial state.
	 */
	const resetForm = () => {
		updateState((draft) => {
			draft.staging = draft.initial;
		});

		setIsSafe();
	};

	/**
	 * Updates the staging state.
	 */
	const updateForm = (newValues: Partial<State>) => {
		updateState((draft) => {
			draft.staging = { ...draft.staging, ...newValues };
		});

		setIsUnsafe();
	};

	/**
	 * Uses the zod schema to validate the staging state. if the state is
	 * invalid, it returns an array of errors that include the key path and
	 * the error message generated by zod.
	 */
	const validate = (): ValidationResult<State> => {
		const result = schema.safeParse(state.staging);

		let errors: Record<string, UseFormError> = {};

		if (result.success === false) {
			errors = result.error.errors.reduce((acc, issue) => {
				const error = {
					keyPath: issue.path,
					message: issue.message,
				};

				const key = error.keyPath.join(".");
				acc[key] = error;
				return acc;
			}, {});
		}

		return {
			data: result.success ? result.data : ({} as State),
			isValid: result.success,
			errors: errors,
		};
	};

	return {
		validate,
		resetForm,
		updateForm,
		setInitialState,
		hasUnsavedChanges: stateHasChanged,
		setNewDefaultValues,
		state: state.staging,
		initialState: state.initial,
		receivedInitialState: receivedInitialState.current,
	};
};

export default useForm;
